import type { AppState } from '../../store/types';
import type {
  AiMetadataBulkReview,
  AiMetadataBulkReviewEntry,
  AiMetadataGeneration,
} from './types';

export const getAiMetadataGenerationKey = (
  assetId: string,
  versionId: string,
): string => `${assetId}|${versionId}`;

export const getAiMetadataGeneration = (
  state: AppState,
  assetId: string | undefined,
  versionId: string | undefined,
): AiMetadataGeneration | undefined => {
  if (!assetId || !versionId) {
    return undefined;
  }
  return state.aiMetadata.generations[
    getAiMetadataGenerationKey(assetId, versionId)
  ];
};

export const findAiMetadataGenerationForAsset = (
  state: AppState,
  assetId: string | undefined,
  predicate?: (entry: AiMetadataGeneration) => boolean,
): AiMetadataGeneration | undefined => {
  if (!assetId) {
    return undefined;
  }
  for (const entry of Object.values(state.aiMetadata.generations)) {
    if (entry.assetId !== assetId) {
      continue;
    }
    if (predicate && !predicate(entry)) {
      continue;
    }
    return entry;
  }
  return undefined;
};

export const findAiMetadataGenerationByJobId = (
  state: AppState,
  jobId: string | undefined,
): AiMetadataGeneration | undefined => {
  if (!jobId) {
    return undefined;
  }
  for (const entry of Object.values(state.aiMetadata.generations)) {
    if (entry.jobId === jobId) {
      return entry;
    }
  }
  return undefined;
};

export const getBulkReview = (
  state: AppState,
): AiMetadataBulkReview | undefined => state.aiMetadata.bulkReview;

export type AiMetadataBulkReviewProgress = {
  total: number;
  updated: number;
  skipped: number;
  remaining: number;
};

// Progress is derived from per-entry statuses rather than from `index`, which is
// no longer monotonic now that entries can be revisited.
export const getBulkReviewProgress = (
  state: AppState,
): AiMetadataBulkReviewProgress => {
  const queue = state.aiMetadata.bulkReview?.queue ?? [];
  let updated = 0;
  let skipped = 0;
  let reviewed = 0;
  for (const entry of queue) {
    if (entry.status === 'updated') {
      updated += 1;
    }
    if (entry.status === 'skipped') {
      skipped += 1;
    }
    if (entry.status) {
      reviewed += 1;
    }
  }
  return {
    total: queue.length,
    updated,
    skipped,
    remaining: queue.length - reviewed,
  };
};

// An entry can be reviewed while it has no terminal outcome: `updated` and
// `discarded` proposals no longer exist server-side, and `error` entries failed
// to load, so opening any of them would strand the modal on a state that never
// resolves.
export const isReviewable = (
  entry: AiMetadataBulkReviewEntry | undefined,
): boolean => !entry?.status || entry.status === 'skipped';

export const findPreviousBulkReviewIndex = (
  state: AppState,
): number | undefined => {
  const bulkReview = state.aiMetadata.bulkReview;
  if (!bulkReview) {
    return undefined;
  }
  for (let index = bulkReview.index - 1; index >= 0; index -= 1) {
    if (isReviewable(bulkReview.queue[index])) {
      return index;
    }
  }
  return undefined;
};

// The next reviewable entry ahead of the cursor, or `queue.length` when there is
// none — that index is what makes the modal show its end-of-bulk screen. Moving
// forward has to skip terminal entries too: stepping back to re-review an
// earlier asset otherwise leaves already-actioned ones between the cursor and
// the end of the queue.
export const findNextBulkReviewIndex = (state: AppState): number => {
  const bulkReview = state.aiMetadata.bulkReview;
  if (!bulkReview) {
    return 0;
  }
  for (
    let index = bulkReview.index + 1;
    index < bulkReview.queue.length;
    index += 1
  ) {
    if (isReviewable(bulkReview.queue[index])) {
      return index;
    }
  }
  return bulkReview.queue.length;
};
